import * as React from "react";
import { useState, useEffect, useRef, useMemo, memo, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getExpandedRowModel,
} from "@tanstack/react-table";
import PreferencesModal from "../products/modals/PreferencesModal";
import { Search } from "lucide-react";
import toast from "react-hot-toast";
import { getCellStyles } from "../../utils/utils";
import { Card, CardContent } from "../../components/ui/card";
import { cn } from "../../lib/utils";
import { useSelector } from "react-redux";
import { Button } from "../ui/button";
import { Play } from "lucide-react";
import { Loader2 } from "lucide-react";
import { X } from "lucide-react";
import { useDispatch } from "react-redux";
import { setSelectedRows } from "../../store/tableSlice";
import Filter from "./table/Filter";
import TooltipWrapper from "./table/TooltipWrapper";

const getCommonPinningStyles = (column) => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn =
    isPinned === "right" && column.getIsFirstColumn("right");

  return {
    boxShadow: isLastLeftPinnedColumn
      ? "-4px 0 4px -4px gray inset"
      : isFirstRightPinnedColumn
      ? "4px 0 4px -4px gray inset"
      : undefined,
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    opacity: 1,
    position: isPinned ? "sticky" : "relative",
    width: column.getSize(),
    zIndex: isPinned ? 2 : undefined,
  };
};

const TableTemplate = ({
  columns,
  data,
  subrowColumns,
  layout = "standard",
  options = {},
  tableClassName,
  headerClassName,
  rowClassName,
  cellClassName,
  containerClassName,
  title,
  titleUrl,
  adLaunchConfig = {},
  minimal = false,
  onServerSearch = null, // Callback for server-side search
  serverSearchValue = "", // Current server search value
  isLoading = false, // Loading state for table overlay
  onServerFilter = null, // Callback for server-side filtering
  serverFilters = [], // Current server filters
  onServerSort = null, // Callback for server-side sorting
  serverSortBy = "", // Current server sort value
  isPaginated = false, // Hide items count for paginated data
}) => {
  // Use object destructuring with a default value
  const {
    enableGlobalFilter = true,
    enablePagination = true,
    enableColumnVisibility = true,
    enableSorting = true,
    enableColumnFilters = true,
    enableColumnOrder = true,
    enableColumnResize = true,
    enableRowSelection = false,
    defaultPageSize = 50,
    showTotalItems = true,
    showPreferences = true,
    enableSubrows = false,
    ...restOptions
  } = options;

  // Table state
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  // Sync local pagination state with server-side pagination
  useEffect(() => {
    if (restOptions.state?.pagination) {
      setPagination(restOptions.state.pagination);
    }
  }, [restOptions.state?.pagination]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnOrder, setColumnOrder] = useState(() =>
    columns.map((column) => column.id)
  );
  const [rowSelection, setRowSelection] = useState({});
  const [columnPinning, setColumnPinning] = useState(() => {
    // Disable pinning on mobile devices
    if (typeof window !== "undefined" && window.innerWidth < 640) {
      return {
        left: [],
        right: [],
      };
    }
    return {
      left: ["rowSelectionColumn"],
      right: ["actions"],
    };
  });
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const numberOfRowsInput = useRef(null);

  // Server-side search state and debouncing
  const [localSearchValue, setLocalSearchValue] = useState(serverSearchValue);
  const searchTimeoutRef = useRef(null);

  // Server-side filter state and debouncing
  const [localFilters, setLocalFilters] = useState(serverFilters);
  const filterTimeoutRef = useRef(null);
  const hasTableInteracted = useRef(false);

  // Server-side sorting timeout ref
  const sortTimeoutRef = useRef(null);

  // Add a trigger to clear all filter states
  const [clearFiltersTriggered, setClearFiltersTriggered] = useState(0);

  // Debounced server search effect
  useEffect(() => {
    if (onServerSearch && localSearchValue !== serverSearchValue) {
      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout for 600ms debounce
      searchTimeoutRef.current = setTimeout(() => {
        onServerSearch(localSearchValue);
      }, 600);
    }

    // Cleanup timeout on unmount
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [localSearchValue, onServerSearch, serverSearchValue]);

  // Update local search value when server search value changes
  useEffect(() => {
    setLocalSearchValue(serverSearchValue);
  }, [serverSearchValue]);

  // Debounced server filter effect
  useEffect(() => {
    if (
      onServerFilter &&
      JSON.stringify(localFilters) !== JSON.stringify(serverFilters) &&
      hasTableInteracted.current
    ) {
      // Clear existing timeout
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }

      // Set new timeout for 600ms debounce
      filterTimeoutRef.current = setTimeout(() => {
        onServerFilter(localFilters);
      }, 600);
    }

    // Cleanup timeout on unmount
    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, [localFilters, onServerFilter, serverFilters]);

  // Update local filters when server filters change
  useEffect(() => {
    setLocalFilters(serverFilters);
  }, [serverFilters]);

  // Initialize sorting state from server on first load
  useEffect(() => {
    if (serverSortBy && !hasTableInteracted.current) {
      const desc = serverSortBy.startsWith("-");
      const id = desc ? serverSortBy.substring(1) : serverSortBy;
      setSorting([{ id, desc }]);
    }
  }, [serverSortBy]);

  // Helper function to update server-side filters
  const updateServerFilter = useCallback(
    (field, value, operator = "like") => {
      hasTableInteracted.current = true;

      if (!onServerFilter) {
        return;
      }

      setLocalFilters((prevFilters) => {
        let newFilters = [...prevFilters];

        if (operator === "range" && Array.isArray(value)) {
          // For range filters, remove existing range filters for this field (>= and <=)
          newFilters = newFilters.filter(
            (filter) =>
              !(
                filter.field === field &&
                (filter.op === ">=" || filter.op === "<=")
              )
          );
          // Add new range filters
          newFilters = newFilters.concat(value);
        } else {
          // For single value filters, remove existing filter with same field and operator
          newFilters = newFilters.filter(
            (filter) => !(filter.field === field && filter.op === operator)
          );

          // Add new filter if value is not empty
          if (value && value.trim() !== "") {
            const newFilter = {
              field: field,
              op: operator,
              value: value.trim(),
            };
            newFilters.push(newFilter);
          }
        }

        return newFilters;
      });
    },
    [onServerFilter]
  );

  const table = useReactTable({
    columns,
    data,
    state: {
      pagination,
      globalFilter,
      columnVisibility,
      columnOrder,
      rowSelection,
      columnPinning,
      // Allow overriding state from restOptions for server-side pagination
      ...restOptions.state,
      // Ensure sorting state takes precedence for server-side sorting
      sorting,
    },
    columnResizeMode: enableColumnResize ? "onChange" : undefined,
    enableSorting: enableSorting,
    enableColumnFilters: enableColumnFilters,
    enableGlobalFilter: enableGlobalFilter,
    enableRowSelection: enableRowSelection,
    autoResetPageIndex: false,
    autoResetExpanded: false,
    // Configure server-side sorting (we handle sorting externally on the server)
    manualSorting: !!onServerSort,
    defaultColumn: {
      minSize: 80,
    },

    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onSortingChange: (updater) => {
      if (onServerSort) {
        hasTableInteracted.current = true;
      }
      const newSorting =
        typeof updater === "function" ? updater(sorting) : updater;
      setSorting(newSorting);
    },
    onPaginationChange: restOptions.onPaginationChange || setPagination, // Use server-side handler if provided
    onRowSelectionChange: setRowSelection,
    onColumnPinningChange: setColumnPinning,

    getCoreRowModel: getCoreRowModel(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    ...(enableSorting && { getSortedRowModel: getSortedRowModel() }),
    ...(enableColumnFilters && {
      getFacetedUniqueValues: getFacetedUniqueValues(),
    }),
    ...(enablePagination &&
      !restOptions.manualPagination && {
        getPaginationRowModel: getPaginationRowModel(),
      }),
    ...(enableGlobalFilter && {
      getFilteredRowModel: getFilteredRowModel(),
    }),
    ...(enableSubrows && {
      getExpandedRowModel: getExpandedRowModel(),
      getRowCanExpand: (row) => row.original.subrows,
    }),
    // Apply all other rest options (including server-side pagination config)
    ...restOptions,
  });

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setSelectedRows(table.getSelectedRowModel().rows));
  }, [dispatch, table, rowSelection]);

  // Simple effect: when sorting changes, trigger server call if enabled
  useEffect(() => {
    if (onServerSort && hasTableInteracted.current) {
      // Clear existing timeout
      if (sortTimeoutRef.current) {
        clearTimeout(sortTimeoutRef.current);
      }

      // Set new timeout for 300ms debounce
      sortTimeoutRef.current = setTimeout(() => {
        onServerSort(sorting);
      }, 300);
    }

    // Cleanup timeout on unmount
    return () => {
      if (sortTimeoutRef.current) {
        clearTimeout(sortTimeoutRef.current);
      }
    };
  }, [sorting, onServerSort, table]);

  // Handle pagination bounds when data changes
  useEffect(() => {
    const totalPages = table.getPageCount();
    const currentPage = pagination.pageIndex;

    // If current page exceeds available pages, go to last page
    if (totalPages > 0 && currentPage >= totalPages) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: Math.max(0, totalPages - 1),
      }));
    }
  }, [data, pagination.pageIndex, table]);

  // Handle responsive column pinning
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        // Disable pinning on mobile
        setColumnPinning({
          left: [],
          right: [],
        });
      } else {
        // Enable pinning on desktop
        setColumnPinning({
          left: ["rowSelectionColumn"],
          right: ["actions"],
        });
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getPageNumbers = () => {
    const totalPages = table.getPageCount();
    const currentPage = table.getState().pagination.pageIndex + 1; // Use table's current state
    const pageNumbers = [];

    // If no pages or only 1 page, return simple array
    if (totalPages <= 1) {
      return totalPages === 1 ? [1] : [];
    }

    // Always show first page
    pageNumbers.push(1);

    // If current page is in the first few pages (1, 2, 3, 4)
    if (currentPage <= 4) {
      // Show 1, 2, 3, 4, ..., last
      for (let i = 2; i <= Math.min(4, totalPages - 1); i++) {
        pageNumbers.push(i);
      }
      if (totalPages > 5) {
        pageNumbers.push("...");
      }
    }
    // If current page is in the last few pages
    else if (currentPage > totalPages - 4) {
      // Show 1, ..., last-3, last-2, last-1, last
      if (totalPages > 5) {
        pageNumbers.push("...");
      }
      for (let i = Math.max(totalPages - 3, 2); i <= totalPages - 1; i++) {
        pageNumbers.push(i);
      }
    }
    // Current page is in the middle
    else {
      // Show 1, ..., current-1, current, current+1, ..., last
      pageNumbers.push("...");
      pageNumbers.push(currentPage - 1);
      pageNumbers.push(currentPage);
      pageNumbers.push(currentPage + 1);
      pageNumbers.push("...");
    }

    // Always show last page (if more than 1 page)
    if (totalPages > 1) {
      pageNumbers.push(totalPages);
    }

    // Remove duplicates while preserving order
    const seen = new Set();
    return pageNumbers.filter((num) => {
      if (seen.has(num)) {
        return false;
      }
      seen.add(num);
      return true;
    });
  };

  const renderKeyValueLayout = () => {
    const item = data[0] || {};

    return (
      <div className="bg-white rounded-lg">
        {title &&
          (titleUrl ? (
            <a
              href={titleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-3xl font-bold text-amber-700 mb-2 block"
            >
              {title.display}
            </a>
          ) : (
            <h1 className="text-3xl font-bold text-amber-700 mb-2">
              {title.display}
            </h1>
          ))}

        <div className="border border-gray-200 rounded-lg  overflow-x-auto md:overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <tbody className="divide-y divide-gray-200">
              {table.getRowModel().rows.map((row) =>
                row.getVisibleCells().map((cell, index) => (
                  <tr
                    key={cell.id}
                    className={index % 2 === 0 ? "bg-gray-50" : ""}
                  >
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">
                      {flexRender(
                        cell.column.columnDef.header,
                        cell.getContext()
                      )}
                    </td>
                    <td
                      className="px-6 py-3 text-sm text-gray-900"
                      style={{
                        ...getCellStyles(cell.column.id, item),
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i];
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.id}--size`] = header.column.getSize();
    }

    return colSizes;
  }, [table.getState().columnSizingInfo, table.getState().columnSizing]);

  const renderStandardLayout = () => (
    <div className="space-y-4">
      {!minimal && (
        <div className="flex flex-col gap-4 pb-4 mt-1 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex-shrink-0">
            {showTotalItems && !isPaginated && (
              <div className="text-sm text-gray-500">
                {table.getRowCount() || 0} items found
                {enableRowSelection && (
                  <span className="ml-2">
                    ({Object.keys(rowSelection).length} selected)
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            {enableGlobalFilter && (
              <div className="relative w-full sm:w-[250px] md:w-[300px] lg:w-[350px]">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search in all fields..."
                  className="pl-10 pr-8 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={
                    onServerSearch
                      ? localSearchValue
                      : table.getState().globalFilter || ""
                  }
                  onChange={(e) => {
                    if (onServerSearch) {
                      // Server-side search
                      setLocalSearchValue(e.target.value);
                    } else {
                      // Client-side search
                      table.setGlobalFilter(String(e.target.value));
                    }
                  }}
                />
                {(onServerSearch
                  ? localSearchValue
                  : table.getState().globalFilter) && (
                  <button
                    onClick={() => {
                      if (onServerSearch) {
                        setLocalSearchValue("");
                      } else {
                        table.setGlobalFilter("");
                      }
                    }}
                    className="absolute right-2 top-2.5 p-0.5 rounded-full hover:bg-gray-100 transition-colors"
                    type="button"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
              {enableColumnFilters && (
                <button
                  onClick={() => {
                    // Clear client-side filters
                    table.resetColumnFilters();

                    // Clear server-side search
                    if (onServerSearch) {
                      setLocalSearchValue("");
                    } else {
                      table.setGlobalFilter("");
                    }

                    // Clear server-side filters
                    if (onServerFilter) {
                      hasTableInteracted.current = true;
                      setLocalFilters([]);
                      // Trigger filter components to clear their internal state
                      setClearFiltersTriggered((prev) => prev + 1);
                    }

                    // Clear server-side sorting
                    if (onServerSort) {
                      hasTableInteracted.current = true;
                      setSorting([]);
                    } else {
                      // Clear client-side sorting
                      table.resetSorting();
                    }
                  }}
                  className="flex-shrink-0 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 w-full sm:w-auto"
                >
                  <span className="sm:inline">Clear All Filters</span>
                </button>
              )}

              {showPreferences && (
                <button
                  className="flex-shrink-0 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 w-full sm:w-auto"
                  onClick={() => setIsPreferencesOpen(true)}
                >
                  <span className="sm:inline">Preferences</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg">
        <div className="relative w-full">
          <div
            className={`overflow-x-auto overflow-y-auto ${
              containerClassName || "max-h-[950px]"
            } min-h-40 border border-gray-200 rounded-lg`}
          >
            <div className="min-w-full">
              <table
                className={
                  tableClassName ||
                  "w-full table-fixed border-collapse border-spacing-0"
                }
                style={{
                  ...(enableColumnResize
                    ? {
                        ...columnSizeVars,
                        width: table.getTotalSize(),
                      }
                    : {}),
                }}
              >
                <thead
                  className={`${
                    headerClassName || "bg-gray-50"
                  } sticky top-0 z-30`}
                >
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="text-nowrap">
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={cn(
                            "px-6 py-3 text-left text-sm font-medium text-black bg-[#F7F6F7] border border-gray-200 relative h-12"
                          )}
                          style={{
                            width: `calc(var(--header-${header?.id}-size) * 1px)`,
                            ...getCommonPinningStyles(header.column),
                          }}
                        >
                          <TooltipWrapper
                            tipText={
                              header.column.columnDef.meta?.columnInfo?.tip_text
                            }
                          >
                            <div className="flex items-center justify-between gap-2 min-w-0">
                              <div
                                className={cn(
                                  "flex items-center gap-2 truncate",
                                  header.column.getCanSort()
                                    ? "cursor-pointer select-none"
                                    : ""
                                )}
                                onClick={(e) => {
                                  // Don't allow sorting if column can't be sorted
                                  if (!header.column.getCanSort()) {
                                    return;
                                  }

                                  if (onServerSort) {
                                    // Custom server-side sorting toggle logic
                                    const currentSort = sorting.find(
                                      (s) => s.id === header.column.id
                                    );
                                    let newSorting = [];

                                    if (!currentSort) {
                                      // No current sort → asc
                                      newSorting = [
                                        { id: header.column.id, desc: false },
                                      ];
                                    } else if (!currentSort.desc) {
                                      // Currently asc → desc
                                      newSorting = [
                                        { id: header.column.id, desc: true },
                                      ];
                                    } else {
                                      // Currently desc → none (empty array)
                                      newSorting = [];
                                    }

                                    // Call the onSortingChange handler to trigger server call
                                    const onSortingChangeHandler =
                                      table.options.onSortingChange;
                                    if (onSortingChangeHandler) {
                                      onSortingChangeHandler(newSorting);
                                    }
                                  } else {
                                    // Use default client-side sorting handler
                                    const handler =
                                      header.column.getToggleSortingHandler();
                                    if (handler) {
                                      handler(e);
                                    }
                                  }
                                }}
                              >
                                <div className="flex-shrink-0">
                                  {(() => {
                                    // For server-side sorting, use component sorting state
                                    // For client-side sorting, use table's internal state
                                    const sortState = onServerSort
                                      ? (() => {
                                          const currentSort = sorting.find(
                                            (s) => s.id === header.column.id
                                          );
                                          return currentSort
                                            ? currentSort.desc
                                              ? "desc"
                                              : "asc"
                                            : false;
                                        })()
                                      : header.column.getIsSorted();

                                    const icons = {
                                      asc: " ⬆️",
                                      desc: " ⬇️",
                                    };
                                    return icons[sortState] ?? null;
                                  })()}
                                </div>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </div>
                              {enableColumnFilters &&
                                header.column.getCanFilter() && (
                                  <div className="flex-shrink-0">
                                    <Filter
                                      column={header.column}
                                      onServerFilter={
                                        onServerFilter
                                          ? updateServerFilter
                                          : null
                                      }
                                      clearFiltersTriggered={
                                        clearFiltersTriggered
                                      }
                                    />
                                  </div>
                                )}
                            </div>
                          </TooltipWrapper>
                          {enableColumnResize && (
                            <div
                              onDoubleClick={() => header.column.resetSize()}
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              className="absolute right-0 top-0 h-full w-1 bg-gray-300 cursor-col-resize opacity-0 hover:opacity-100"
                            />
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                {isLoading ? (
                  <tbody>
                    <tr>
                      <td
                        colSpan={table.getAllColumns().length}
                        className="text-center py-16"
                      >
                        <div className="flex flex-col items-center gap-3">
                          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                          <p className="text-sm text-gray-600">Loading...</p>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <MemoizedTableBody
                    table={table}
                    data={data}
                    rowClassName={rowClassName}
                    cellClassName={cellClassName}
                    enableRowSelection={enableRowSelection}
                    pageIndex={table.getState().pagination.pageIndex}
                    pageSize={table.getState().pagination.pageSize}
                    globalSearchState={table.getState().globalFilter}
                    subrowColumns={subrowColumns}
                  />
                )}
              </table>
            </div>
          </div>
        </div>

        {!table.getIsSomeColumnsVisible() && (
          <div className="text-center w-full p-4">
            Please make at least one column visible
          </div>
        )}
      </div>

      {/* just live the ad button, with almost the same styling, we're gonna have a button here that says "Launch all selected" */}
      {adLaunchConfig.enableAdLaunch && (
        <div>
          <Button
            variant="ghost"
            size="default"
            className="outline outline-1"
            onClick={() =>
              adLaunchConfig?.onAdLaunch(table.getSelectedRowModel().rows)
            }
            disabled={adLaunchConfig.isLoading}
          >
            {!adLaunchConfig.isLoading ? (
              <Play className="w-4 h-4" />
            ) : (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
            <span>Launch all selected</span>
          </Button>
        </div>
      )}

      {!minimal && enablePagination && table.getIsSomeColumnsVisible() && (
        <div className="flex flex-col gap-4 py-4 rounded-lg shadow-sm lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium text-gray-700 text-center lg:text-left">
              Page {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </p>
            <div className="flex flex-col gap-3 items-center sm:flex-row sm:justify-center lg:justify-start">
              <span className="text-sm font-medium text-gray-600 text-nowrap">
                Rows per page
              </span>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <input
                  type="number"
                  min="10"
                  defaultValue={table.getState().pagination.pageSize}
                  className="w-16 sm:w-20 px-1.5 sm:px-2 py-1 text-xs sm:text-sm text-gray-900 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="10"
                  ref={numberOfRowsInput}
                />
                <button
                  onClick={() => {
                    if (numberOfRowsInput) {
                      const size = parseInt(numberOfRowsInput.current.value);
                      if (isNaN(size)) {
                        toast.error("Page size must be a valid number");
                        return;
                      }
                      if (size < 10) {
                        toast.error("Page size must be at least 10 rows");
                        return;
                      }
                      table.setPageSize(size);
                    }
                  }}
                  className="inline-flex items-center px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <span className="sm:inline">Update</span>
                </button>
              </div>
            </div>
          </div>

          <nav className="flex justify-center lg:justify-end">
            <div className="inline-flex items-center gap-0.5 sm:gap-1">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  table.previousPage();
                }}
                disabled={!table.getCanPreviousPage()}
                className="relative inline-flex items-center px-2 py-1.5 sm:px-3 sm:py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <span className="sr-only">Previous</span>
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              <div className="inline-flex -space-x-px">
                {getPageNumbers()
                  .slice(
                    0,
                    window.innerWidth < 640 ? 5 : getPageNumbers().length
                  )
                  .map((pageNumber, index) => (
                    <button
                      key={pageNumber}
                      onClick={(e) => {
                        e.preventDefault();
                        pageNumber !== "..." &&
                          table.setPageIndex(pageNumber - 1);
                      }}
                      disabled={pageNumber === "..."}
                      className={`relative inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium transition-colors
                      ${
                        pageNumber === table.getState().pagination.pageIndex + 1
                          ? "z-10 bg-blue-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          : pageNumber === "..."
                          ? "text-gray-700 bg-white cursor-default"
                          : "text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      } ${index === 0 ? "rounded-l-md" : ""} ${
                        index ===
                        (window.innerWidth < 640
                          ? Math.min(5, getPageNumbers().length)
                          : getPageNumbers().length) -
                          1
                          ? "rounded-r-md"
                          : ""
                      } border border-gray-300`}
                    >
                      {pageNumber}
                    </button>
                  ))}
              </div>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  table.nextPage();
                }}
                disabled={!table.getCanNextPage()}
                className="relative inline-flex items-center px-2 py-1.5 sm:px-3 sm:py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <span className="sr-only">Next</span>
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </nav>
        </div>
      )}

      {!minimal && PreferencesModal && isPreferencesOpen && (
        <PreferencesModal
          table={table}
          isOpen={isPreferencesOpen}
          onClose={() => setIsPreferencesOpen(false)}
          setColumnOrder={setColumnOrder}
        />
      )}
    </div>
  );

  const renderCardLayout = () => {
    const findImageColumn = (columns) => {
      return columns.find((col) => col.columnDef.meta?.isImageColumn)?.id;
    };
    const imageColumnKey = findImageColumn(table.getAllColumns());

    return (
      <div className="space-y-4">
        {/* Search and Controls Section */}
        <div className="flex flex-col gap-4 pb-4 mt-1 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex-shrink-0">
            {showTotalItems && (
              <div className="text-sm text-gray-500 text-center lg:text-left">
                {table.getRowCount() || 0} items found
                {enableRowSelection && (
                  <span className="ml-2">
                    ({Object.keys(rowSelection).length} selected)
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            {enableGlobalFilter && (
              <div className="relative w-full sm:w-[250px] md:w-[300px] lg:w-[350px]">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search in all fields..."
                  className="pl-10 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  onChange={(e) =>
                    table.setGlobalFilter(String(e.target.value))
                  }
                />
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
              {enableColumnFilters && (
                <button
                  onClick={() => {
                    // Clear client-side filters
                    table.resetColumnFilters();

                    // Clear server-side search
                    if (onServerSearch) {
                      setLocalSearchValue("");
                    } else {
                      table.setGlobalFilter("");
                    }

                    // Clear server-side filters
                    if (onServerFilter) {
                      hasTableInteracted.current = true;
                      setLocalFilters([]);
                      // Trigger filter components to clear their internal state
                      setClearFiltersTriggered((prev) => prev + 1);
                    }

                    // Clear server-side sorting
                    if (onServerSort) {
                      hasTableInteracted.current = true;
                      setSorting([]);
                    } else {
                      // Clear client-side sorting
                      table.resetSorting();
                    }
                  }}
                  className="flex-shrink-0 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 w-full sm:w-auto"
                >
                  <span className="sm:inline">Clear All Filters</span>
                </button>
              )}

              {showPreferences && (
                <button
                  className="flex-shrink-0 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 w-full sm:w-auto"
                  onClick={() => setIsPreferencesOpen(true)}
                >
                  <span className="sm:inline">Preferences</span>
                </button>
              )}

              {enableRowSelection && (
                <div className="flex items-center justify-center sm:justify-start gap-2 p-2 bg-gray-50 rounded-md">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={table.getIsAllPageRowsSelected()}
                      onChange={table.getToggleAllRowsSelectedHandler()}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-nowrap">Select All</span>
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="relative">
          {/* Loading Overlay for Card Layout */}
          {isLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50 rounded-lg">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <p className="text-sm text-gray-600">Loading...</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {table.getRowModel().rows.map((row) => (
              <Card
                className={`overflow-hidden hover:shadow-lg transition-shadow cursor-pointer ${
                  row.getIsSelected() ? "ring-2 ring-blue-500" : ""
                }`}
                key={row.id}
                {...(enableRowSelection
                  ? {
                      onClick: () => {
                        row.getToggleSelectedHandler();
                      },
                    }
                  : {})}
              >
                {/* Selection overlay */}
                {enableRowSelection && (
                  <div className="absolute top-2 right-2 z-10">
                    <div className="bg-white rounded-full p-1 shadow">
                      <input
                        type="checkbox"
                        checked={row.getIsSelected()}
                        onChange={row.getToggleSelectedHandler()}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                )}

                {/* image section */}
                {imageColumnKey && (
                  <div className="w-full h-[25rem] relative bg-gray-100 overflow-hidden">
                    {row?.original[imageColumnKey]?.display?.endsWith(
                      ".mp4"
                    ) ? (
                      <video
                        src={row.original[imageColumnKey].display}
                        className="w-full h-full object-cover pointer-events-none"
                        autoPlay
                        loop
                        muted
                        playsInline
                        // We remove controls to keep it looking like an image
                        // But add pointer-events-none to prevent any user interaction
                      />
                    ) : (
                      <img
                        src={row.original[imageColumnKey].display}
                        alt={row.original.name?.display || "Product"}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="space-y-2">
                    {row.getVisibleCells().map((cell) => {
                      if (cell.column.id === imageColumnKey) return null;
                      if (cell.column.columnDef.meta?.excludeFromCard)
                        return null;

                      return (
                        <div
                          key={cell.id}
                          className="flex justify-between items-start"
                        >
                          <TooltipWrapper
                            tipText={
                              cell.column.columnDef.meta?.columnInfo?.tip_text
                            }
                          >
                            <span className="text-sm font-medium text-gray-500 text-left w-2/4">
                              {cell.column.columnDef.meta?.columnInfo?.display}
                            </span>
                          </TooltipWrapper>
                          <span className="text-sm text-gray-900 text-right w-2/4 ml-auto force-right">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Pagination Section */}
        {enablePagination && (
          <div className="flex flex-col gap-4 px-4 py-4  rounded-lg shadow-sm lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium text-gray-700 text-center lg:text-left">
                Page {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount()}
              </p>
              <div className="flex flex-col gap-3 items-center sm:flex-row sm:justify-center lg:justify-start">
                <span className="text-sm font-medium text-gray-600 text-nowrap">
                  Items per page
                </span>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <input
                    type="number"
                    min="10"
                    defaultValue={table.getState().pagination.pageSize}
                    className="w-16 sm:w-20 px-1.5 sm:px-2 py-1 text-xs sm:text-sm text-gray-900 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="10"
                    ref={numberOfRowsInput}
                  />
                  <button
                    onClick={() => {
                      if (numberOfRowsInput) {
                        const size = parseInt(numberOfRowsInput.current.value);
                        if (isNaN(size)) {
                          toast.error("Page size must be a valid number");
                          return;
                        }
                        if (size < 10) {
                          toast.error("Page size must be at least 10 rows");
                          return;
                        }
                        table.setPageSize(size);
                      }
                    }}
                    className="inline-flex items-center px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <span className="sm:inline">Update</span>
                  </button>
                </div>
              </div>
            </div>

            <nav className="inline-flex items-center gap-0.5 sm:gap-1">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  table.previousPage();
                }}
                disabled={!table.getCanPreviousPage()}
                className="relative inline-flex items-center px-2 py-1.5 sm:px-3 sm:py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <span className="sr-only">Previous</span>
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              <div className="inline-flex -space-x-px">
                {getPageNumbers()
                  .slice(
                    0,
                    window.innerWidth < 640 ? 5 : getPageNumbers().length
                  )
                  .map((pageNumber, index) => (
                    <button
                      key={pageNumber}
                      onClick={(e) => {
                        e.preventDefault();
                        pageNumber !== "..." &&
                          table.setPageIndex(pageNumber - 1);
                      }}
                      disabled={pageNumber === "..."}
                      className={`relative inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium transition-colors
                      ${
                        pageNumber === table.getState().pagination.pageIndex + 1
                          ? "z-10 bg-blue-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          : pageNumber === "..."
                          ? "text-gray-700 bg-white cursor-default"
                          : "text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      } ${index === 0 ? "rounded-l-md" : ""} ${
                        index ===
                        (window.innerWidth < 640
                          ? Math.min(5, getPageNumbers().length)
                          : getPageNumbers().length) -
                          1
                          ? "rounded-r-md"
                          : ""
                      } border border-gray-300`}
                    >
                      {pageNumber}
                    </button>
                  ))}
              </div>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  table.nextPage();
                }}
                disabled={!table.getCanNextPage()}
                className="relative inline-flex items-center px-2 py-1.5 sm:px-3 sm:py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <span className="sr-only">Next</span>
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </nav>
          </div>
        )}

        {/* Preferences Modal */}
        {PreferencesModal && isPreferencesOpen && (
          <PreferencesModal
            table={table}
            isOpen={isPreferencesOpen}
            onClose={() => setIsPreferencesOpen(false)}
            setColumnOrder={setColumnOrder}
          />
        )}
      </div>
    );
  };

  return layout === "keyValue"
    ? renderKeyValueLayout()
    : layout === "card"
    ? renderCardLayout()
    : renderStandardLayout();
};

const MemoizedTableBody = memo(
  function MemoizedTableBody({
    table,
    rowClassName,
    cellClassName,
    subrowColumns,
  }) {
    const activeLongTextColumn = useSelector(
      (state) => state.table.activeLongTextCell
    );
    const activeHoverImage = useSelector(
      (state) => state.table.activeHoverImage
    );

    return (
      <tbody className="divide-y divide-gray-200">
        {table.getRowModel().rows.map((row) => {
          return (
            <React.Fragment key={row.id}>
              <tr
                className={`${
                  rowClassName || "hover:bg-gray-50 transition-colors"
                } ${row.getIsSelected() ? "bg-blue-50" : ""}`}
              >
                {row.getVisibleCells().map((cell) => {
                  const isNavigable = cell?.column?.columnDef?.meta?.navigable;
                  const isClickable = cell?.column?.columnDef?.meta?.clickable;

                  // Add persistent visual indicators
                  let interactiveStyles = "";

                  if (isClickable || isNavigable) {
                    interactiveStyles += "bg-gray-50";
                  }

                  // Determine background color based on selection
                  const bgColor = row.getIsSelected()
                    ? "bg-blue-50"
                    : "bg-white";

                  return (
                    <td
                      onClick={() =>
                        cell.column.columnDef.meta?.handlers?.onClick?.(cell)
                      }
                      key={cell.id}
                      className={cn(
                        cellClassName ||
                          `text-sm text-gray-900 border border-gray-200 relative h-12 bg-white px-6 py-2 group ${interactiveStyles} ${bgColor}`
                      )}
                      style={{
                        ...getCellStyles(cell.column.id, cell.row.original),
                        width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
                        ...getCommonPinningStyles(cell.column),

                        // Add z-index when either long text or hover image is active
                        ...((activeLongTextColumn?.rowId === row.id &&
                          activeLongTextColumn?.columnId === cell.column.id) ||
                        (activeHoverImage?.rowId === row.id &&
                          activeHoverImage?.columnId === cell.column.id)
                          ? { zIndex: 999 }
                          : {}),
                      }}
                    >
                      <div className="truncate w-full overflow-hidden h-full flex items-center">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>

              {row.getIsExpanded() && row.original.subrows && (
                <tr>
                  <td
                    colSpan={row.getVisibleCells().length}
                    className="p-0 border-b border-gray-200"
                  >
                    <div className="relative border-l-4 border-blue-400 bg-[#f6f7fb] pt-3 pb-3">
                      {/* Add connecting lines container */}
                      <div className="absolute left-0 top-0 h-full w-10">
                        {row.original.subrows.map((_, index) => (
                          <div
                            key={index}
                            className="absolute -left-px h-6 w-10 border-l-2 border-b-2 border-blue-400 rounded-bl"
                            style={{ top: `${index * 34 + 80}px` }}
                          />
                        ))}
                      </div>
                      <div className="pl-10">
                        <TableTemplate
                          options={{
                            enableRowSelection: true,
                          }}
                          columns={subrowColumns}
                          data={row.original.subrows}
                          minimal={true}
                          tableClassName="w-full border-collapse border-spacing-0"
                          // headerClassName="hidden"
                          rowClassName="hover:bg-gray-50"
                          cellClassName="text-sm text-gray-700 border-0 border-b border-[#e6e9ef] px-6 py-2"
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    );
  },
  (prev, next) => {
    // We need to update when selection changes or sticky columns change
    if (prev.enableRowSelection !== next.enableRowSelection) return false;
    if (prev.stickyColumns !== next.stickyColumns) return false;
    if (prev.globalSearchState !== next.globalSearchState) return false;
    if (prev.pageIndex !== next.pageIndex) return false;
    if (prev.pageSize !== next.pageSize) return false;
    return prev.table.data !== next.table.data;
  }
);

export default TableTemplate;
